import { resolveApiBaseUrl } from '@/soluqr/api/client';
import { logStructured } from '@/lib/networkLog';
import { customerSessionHeaders } from '@/lib/customerSession';
import { clearCustomerSessionToken } from '@/lib/localCustomer';
import { ensureCustomerSessionForUpload } from '@/lib/checkout/ensureCustomerSession';

const MAX_PROOF_BYTES = 5 * 1024 * 1024;
const HEIC_ERROR =
  'Formato HEIC no soportado. En iPhone: Ajustes > Cámara > Formatos > Más compatible, o subí una foto JPG/PNG.';

function isHeicFile(file) {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return /heic|heif/.test(type) || /\.heic$|\.heif$/.test(name);
}

function isPdfFile(file) {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return type === 'application/pdf' || name.endsWith('.pdf');
}

async function maybeConvertHeicToJpeg(file) {
  if (!isHeicFile(file)) return file;
  if (typeof createImageBitmap !== 'function') {
    throw new Error(HEIC_ERROR);
  }
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error(HEIC_ERROR);
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close?.();
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error(HEIC_ERROR))), 'image/jpeg', 0.92);
    });
    const baseName = String(file.name || 'comprobante').replace(/\.(heic|heif)$/i, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  } catch {
    throw new Error(HEIC_ERROR);
  }
}

function validateProofFileBeforeUpload(file) {
  if (!file || !file.size) {
    throw new Error('Archivo vacío');
  }
  if (file.size > MAX_PROOF_BYTES) {
    throw new Error('Imagen muy grande. Máximo 5MB');
  }
  if (isPdfFile(file)) return;
  const type = String(file.type || '').toLowerCase();
  if (type && !type.startsWith('image/') && type !== 'application/octet-stream') {
    throw new Error('Tipo de archivo no válido. Use JPG, PNG, WEBP, GIF o PDF');
  }
}

async function parseUploadError(response) {
  try {
    const data = await response.json();
    const detail = data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail.trim();
    if (Array.isArray(detail) && detail.length) {
      const first = detail[0];
      if (typeof first === 'string') return first;
      if (first?.msg) return String(first.msg);
    }
  } catch {
    // ignore
  }
  if (response.status === 401) {
    return 'No se pudo validar tu sesión. Revisá nombre y teléfono e intentá de nuevo.';
  }
  return 'Error al cargar comprobante';
}

async function postProof(file, businessId, outerSignal, timeoutMs) {
  const apiBase = resolveApiBaseUrl();
  if (!apiBase) {
    logStructured('upload_proof_failed', { phase: 'config', message: 'missing_api_base' });
    throw new Error('Configuración del servidor no disponible');
  }

  const formData = new FormData();
  formData.append('file', file);

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  const onOuterAbort = () => controller.abort();
  if (outerSignal) {
    if (outerSignal.aborted) controller.abort();
    else outerSignal.addEventListener('abort', onOuterAbort, { once: true });
  }

  let response;
  try {
    response = await fetch(`${apiBase}/menu/upload-proof`, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
      headers: businessId ? customerSessionHeaders(businessId) : {},
    });
  } catch (e) {
    if (e?.name === 'AbortError') {
      logStructured('upload_proof_failed', { phase: 'network', name: 'AbortError' });
      const err = new Error('Tiempo de espera agotado al subir el comprobante');
      err.name = 'AbortError';
      throw err;
    }
    logStructured('upload_proof_failed', { phase: 'network', message: e?.message, name: e?.name });
    throw e;
  } finally {
    clearTimeout(t);
    if (outerSignal) outerSignal.removeEventListener('abort', onOuterAbort);
  }

  if (!response.ok) {
    const message = await parseUploadError(response);
    logStructured('upload_proof_failed', { phase: 'http', status: response.status, message });
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }
  const data = await response.json();
  if (!data?.url) {
    logStructured('upload_proof_failed', { phase: 'parse', message: 'missing_url' });
    throw new Error('Respuesta sin URL');
  }
  return data.url;
}

/**
 * Mismo endpoint que CheckoutPage (upload comprobante transferencia).
 * @param {File} file
 * @param {{ timeoutMs?: number, signal?: AbortSignal, businessId?: string, customer?: { name?: string, phone?: string, email?: string, address?: string } }} [opts]
 * @returns {Promise<string>} URL pública del comprobante
 */
export async function uploadMenuOrderProof(file, opts = {}) {
  const timeoutMs = opts.timeoutMs ?? 90000;
  const outerSignal = opts.signal;
  const businessId = opts.businessId;

  validateProofFileBeforeUpload(file);
  let uploadFile = await maybeConvertHeicToJpeg(file);

  const ensureSession = async () => {
    if (businessId && opts.customer) {
      await ensureCustomerSessionForUpload(businessId, opts.customer);
    }
  };

  await ensureSession();

  try {
    return await postProof(uploadFile, businessId, outerSignal, timeoutMs);
  } catch (e) {
    if (e?.status === 401 && businessId) {
      clearCustomerSessionToken(businessId);
      await ensureSession();
      return postProof(uploadFile, businessId, outerSignal, timeoutMs);
    }
    throw e;
  }
}
